import * as actionTypes from "../constants";

import type { MACDropDownRow } from "../scenes/Facility/models/facilities";
import type { PracticeEntity, PracticeTypeDropDownRow } from "../scenes/Practice/models/practices";
import type { PracticesState } from "../scenes/Practice/models/practicesState";

const createEmptyPracticesState = (): PracticesState => ({
  practices: [],
  originalPractices: [],

  practiceTypeDropDown: [],
  MACDropDown: [],

  initialized: false,
  practicesLoading: false,
  practicesSaving: false,
  errorTexts: [],
  showAddForm: false,
  hideInactiveItems: true,
});

export const practicesReducer = (state = createEmptyPracticesState(), action) => {
  switch (action.type) {
    case actionTypes.PRACTICE_GET_PRACTICES_BEGIN:
      return handleGetPracticesStart(state);
    case actionTypes.PRACTICE_GET_PRACTICES_COMPLETED:
      return handleGetPracticesCompleted(state, action.payload);
    case actionTypes.PRACTICE_GET_PRACTICES_FAIL:
      return handleGetPracticesFailed(state, action.payload);
    case actionTypes.PRACTICE_CHANGE:
      return handleChangePractice(state, action.payload);
    case actionTypes.PRACTICE_CONFIRM_CHANGE:
      return handleConfirmChangePractice(state, action.payload);
    case actionTypes.PRACTICE_DISCARD_CHANGE:
      return handleDiscardChangePractice(state, action.payload);
    case actionTypes.PRACTICE_REMOVE:
      return handleRemovePractice(state, action.payload);
    case actionTypes.PRACTICE_CREATE:
      return handleCreatePractice(state, action.payload);
    case actionTypes.PRACTICE_CONFIRM_CREATE:
      return handleConfirmCreate(state, action.payload);
    case actionTypes.PRACTICE_DISCARD_CREATE:
      return handleDiscardCreate(state, action.payload);
    case actionTypes.PRACTICE_DISCARD_ALL_CHANGES:
      return handleDiscardAllChanges(state);
    case actionTypes.PRACTICE_SET_PRACTICE_TYPES_DROPDOWN:
      return handleSetPracticeTypesDropDown(state, action.payload);
    case actionTypes.PRACTICE_SET_MAC_DROPDOWN:
      return handleSetMACDropDown(state, action.payload);
    case actionTypes.PRACTICE_SET_ERROR:
      return handleSetError(state, action.payload);
    case actionTypes.PRACTICE_SHOW_ADD_FORM:
      return handleShowAddForm(state, action.payload);
    case actionTypes.PRACTICE_SET_SAVING:
      return handleSetPracticesSaving(state, action.payload);
    case actionTypes.PRACTICE_TOGGLE_HIDE_INACTIVE_ITEMS:
      return handleToggleHideInactiveItems(state);
    case actionTypes.SETTINGS_CONFIRM_DISCARD_DIRTY_CHANGES:
      return handleClearDirtyChanges(state);

    default:
      return state;
  }
};

const handleGetPracticesStart = (state: PracticesState): PracticesState => ({
  ...state,
  practicesLoading: true,
  errorTexts: [],
});

const handleGetPracticesFailed = (state: PracticesState, error): PracticesState => ({
  ...state,
  practicesLoading: false,
  errorTexts: [error.message],
});

const addMacNameToItem = (practice: PracticeEntity, MACDropDown: MACDropDownRow[]) => {
  if (practice.MacName) {
    return practice;
  }

  if (!practice.Mac) {
    return { ...practice, MacName: "" };
  }

  const macItem = MACDropDown.find((ddlItem) => ddlItem.Id === practice.Mac);
  return {
    ...practice,
    MacName: macItem?.MacName || practice.Mac || "",
  };
};

const addMacNames = (practices: PracticeEntity[], MACDropDown: MACDropDownRow[]) =>
  practices.map((item) => addMacNameToItem(item, MACDropDown));

const handleGetPracticesCompleted = (
  state: PracticesState,
  payload: PracticeEntity[],
): PracticesState => {
  const practicesWithMacNames = addMacNames(payload, state.MACDropDown);
  return {
    ...state,
    initialized: true,
    practicesLoading: false,
    errorTexts: [],
    practices: [...practicesWithMacNames],
    originalPractices: [...practicesWithMacNames],
  };
};

const handleChangePractice = (state: PracticesState, dataItem: PracticeEntity): PracticesState => {
  const newDataItem = addMacNameToItem(dataItem, state.MACDropDown);
  return {
    ...state,
    practices: state.practices.map((item) =>
      item.ViewId === dataItem.ViewId ? { ...newDataItem } : item,
    ),
  };
};

const handleRemovePractice = (state: PracticesState, dataItem: PracticeEntity): PracticesState => ({
  ...state,
  practices: state.practices.filter((item) => item.ViewId !== dataItem.ViewId),
  originalPractices: state.originalPractices.filter((item) => item.ViewId !== dataItem.ViewId),
  errorTexts: [],
});

const handleCreatePractice = (state: PracticesState, dataItem: PracticeEntity): PracticesState => {
  const newDataItem = addMacNameToItem(dataItem, state.MACDropDown);
  return {
    ...state,
    practices: [newDataItem, ...state.practices],
    errorTexts: [],
  };
};

const handleConfirmCreate = (
  state: PracticesState,
  payload: { createdItemViewId: string; practice: PracticeEntity },
): PracticesState => {
  const rawItem = { ...payload.practice, createdItemViewId: "", inEdit: undefined };
  const newItem = addMacNameToItem(rawItem, state.MACDropDown);

  if (!state.showAddForm) {
    return {
      ...state,
      practices: state.practices.map((item) =>
        item.ViewId === payload.createdItemViewId ? newItem : item,
      ),
      originalPractices: [{ ...newItem }, ...state.originalPractices],
      errorTexts: [],
    };
  }

  return {
    ...state,
    practices: [...state.practices, { ...newItem }],
    originalPractices: [...state.originalPractices, { ...newItem }],
    errorTexts: [],
    showAddForm: false,
  };
};

const handleDiscardCreate = (state: PracticesState, dataItem: PracticeEntity): PracticesState => ({
  ...state,
  practices: state.practices.filter((item) => item.ViewId !== dataItem.ViewId),
  errorTexts: [],
});

const handleConfirmChangePractice = (
  state: PracticesState,
  dataItem: PracticeEntity,
): PracticesState => {
  const rawItem = { ...dataItem, inEdit: undefined };
  const newItem = addMacNameToItem(rawItem, state.MACDropDown);

  return {
    ...state,
    practices: state.practices.map((item) => (item.ViewId === dataItem.ViewId ? newItem : item)),
    originalPractices: state.originalPractices.map((item) =>
      item.ViewId === dataItem.ViewId ? { ...newItem } : item,
    ),
    errorTexts: [],
  };
};

const handleDiscardChangePractice = (
  state: PracticesState,
  dataItem: PracticeEntity,
): PracticesState => {
  const original = state.originalPractices.find((item) => item.ViewId === dataItem.ViewId);
  if (!original) {
    return {
      ...state,
      practices: state.practices.filter((item) => item.ViewId !== dataItem.ViewId),
      errorTexts: [],
    };
  }

  return {
    ...state,
    practices: state.practices.map((item) =>
      item.ViewId === original.ViewId ? { ...original } : item,
    ),
    errorTexts: [],
  };
};

const handleDiscardAllChanges = (state: PracticesState): PracticesState => ({
  ...state,
  practices: [...state.originalPractices],
  errorTexts: [],
});

const handleSetPracticeTypesDropDown = (
  state: PracticesState,
  payload: PracticeTypeDropDownRow[],
): PracticesState => ({
  ...state,
  practiceTypeDropDown: [...payload],
});

const handleSetMACDropDown = (state: PracticesState, payload: MACDropDownRow[]): PracticesState => {
  const defaultRecord = {
    Mac: "",
    Id: "",
    MacName: "<Default for ZIP Code>",
    State: "",
  };

  return {
    ...state,
    MACDropDown: [defaultRecord, ...payload],
  };
};

const handleSetError = (state: PracticesState, errorTexts: string[]): PracticesState => ({
  ...state,
  errorTexts,
});

const handleShowAddForm = (state: PracticesState, showAddForm: boolean): PracticesState => ({
  ...state,
  showAddForm,
});

const handleSetPracticesSaving = (
  state: PracticesState,
  practicesSaving: boolean,
): PracticesState => ({
  ...state,
  practicesSaving,
});

const handleToggleHideInactiveItems = (state: PracticesState): PracticesState => ({
  ...state,
  hideInactiveItems: !state.hideInactiveItems,
});

const handleClearDirtyChanges = (state: PracticesState): PracticesState => ({
  ...state,
  showAddForm: false,
});
